import React, { memo, useMemo } from 'react';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import { PiSmileySad } from 'react-icons/pi';
import { IoChevronUp, IoChevronDown, IoSwapVertical } from 'react-icons/io5';

const MostSoldProductsTable = memo(
  ({
    data = [],
    isLoading = false,
    error = null,
    pagination = {},
    setSearchParams,
    page = 1,
    limit = 10,
    columnName = 'sum_quantity',
    order = 'desc',
  }) => {
    const columnMapping = {
      sales: 'sum_total_amount',
      cost: 'sum_cost',
      profit: 'sum_profit',
      quantity: 'sum_quantity',
    };

    const transformedData = useMemo(() => {
      return (data || []).map((item) => ({
        id: item.id,
        code: item.code || '-',
        name: item.name,
        sales: item.sum_total_amount,
        cost: item.sum_cost,
        profit: item.sum_profit,
        quantity: item.sum_quantity,
      }));
    }, [data]);

    const handleSort = (key) => {
      const apiColumnName = columnMapping[key];
      const newOrder = columnName === apiColumnName && order === 'desc' ? 'asc' : 'desc';
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('columnName', apiColumnName);
        next.set('order', newOrder);
        next.set('page', '1');
        return next;
      });
    };

    const getSortIcon = (key) => {
      const apiColumnName = columnMapping[key];
      if (columnName !== apiColumnName) return <IoSwapVertical size={14} />;
      return order === 'asc' ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />;
    };

    const handleChangePage = (event, newPage) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(newPage + 1));
        return next;
      });
    };

    const handleChangeRowsPerPage = (event) => {
      const newLimit = parseInt(event?.target?.value, 10) || 10;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('limit', String(newLimit));
        next.set('page', '1');
        return next;
      });
    };

    const columns = [
      { id: 'code', label: 'Code', minWidth: 80 },
      { id: 'name', label: 'Name', minWidth: 150 },
      {
        id: 'sales',
        label: 'Sales',
        minWidth: 100,
        format: (value) => value?.toLocaleString(),
        sortable: true,
        sortIcon: getSortIcon('sales'),
        onSort: () => handleSort('sales'),
      },
      {
        id: 'cost',
        label: 'Cost',
        minWidth: 100,
        format: (value) => value?.toLocaleString(),
        sortable: true,
        sortIcon: getSortIcon('cost'),
        onSort: () => handleSort('cost'),
      },
      {
        id: 'profit',
        label: 'Profit',
        minWidth: 100,
        format: (value) => value?.toLocaleString(),
        sortable: true,
        sortIcon: getSortIcon('profit'),
        onSort: () => handleSort('profit'),
      },
      {
        id: 'quantity',
        label: 'Quantity',
        minWidth: 100,
        format: (value) => value?.toLocaleString(),
        sortable: true,
        sortIcon: getSortIcon('quantity'),
        onSort: () => handleSort('quantity'),
      },
    ];

    const totalCount = pagination?.totalRecords ?? 0;

    if (isLoading) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.noData}>
            <PiSmileySad className={styles.noDataIcon} aria-hidden />
            <p>Error loading data: {error}</p>
          </div>
        </div>
      );
    }

    if (!transformedData.length) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.noData}>
            <PiSmileySad className={styles.noDataIcon} aria-hidden />
            <p>No products found.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Table
          columns={columns}
          rows={transformedData}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={totalCount}
          disableInternalPagination
        />
      </div>
    );
  }
);

export default MostSoldProductsTable;
